/**
 * frontend/src/pages/valuation/_SignOffGates.tsx
 *
 * VALUATION-B3 sign-off — the gate wall, and the two actions it guards.
 *
 * The gates are not decoration and they are not the enforcement. Every one of
 * them is also checked server-side: `/finalize` refuses a body the engine would
 * not stand behind, and `can_produce_document` is computed on the snapshot, not
 * here. What this file does is tell the valuator WHY a button is disabled
 * before they press it, because a button that silently does nothing teaches a
 * user to distrust the screen.
 *
 * One gate deserves a note. `preflight.ok` is the same computation `/finalize`
 * will run, but never on the rationale — the rationale changes no number, so
 * typing it does not re-trigger `preflight.ok`. A blank rationale on a tuned
 * methodology is instead a `canFinalize` condition ValuationSignOff.tsx checks
 * directly (`blockedReason`: "Write the reason for this version."); `/finalize`
 * still refuses a blank one strictly, server-side, at the point of freezing.
 *
 * Split out of ValuationSignOff.tsx under docs/FILE_SIZE_POLICY.md.
 */
import { EmailDocumentButton } from './_EmailDocument'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { ROLE_LABELS } from '@/components/AppShell'
import { Link } from 'react-router-dom'
import { NEXT_STEP_LABELS } from './_nextStep'
import { nextStepPath } from './_shared'
import type { ValuationDocumentRow } from '@/api/valuation'
import { IssuedDocumentList } from './_OverviewDocuments'
import type { PreflightResponse } from '@/types/valuationB3'

/** The server's key-people blocker, word for word (core/valuation/company_profile.py KEY_PEOPLE_BLOCK_TEXT). */
export const KEY_PEOPLE_BLOCK_TEXT =
  "Add at least one key person before you finalize. The operating agreement doesn't name anyone, so enter their first name, last name, role and phone or email."

/** One gate row: satisfied, blocked, or not determinable from this session. */
export function Gate({
  state,
  label,
  detail,
}: {
  state: 'ok' | 'blocked' | 'unknown'
  label: string
  detail: string
}) {
  const mark = state === 'ok' ? '✓' : state === 'blocked' ? '!' : '?'
  const tone =
    state === 'ok' ? 'border-green-200 bg-green-50 text-green-900'
      : state === 'blocked' ? 'border-amber-300 bg-amber-50 text-amber-900'
        : 'border-gray-200 bg-gray-50 text-gray-700'
  return (
    <div className={`flex gap-3 rounded-lg border p-3 ${tone}`}>
      <span className="font-bold" aria-hidden>{mark}</span>
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="mt-0.5 text-xs opacity-90">{detail}</p>
      </div>
    </div>
  )
}

export function SignOffGates({
  hasComputedValue,
  concluded,
  canConclude,
  userName,
  userRole,
  hasTuningInputs,
  preflight,
  preflightError,
  onBackToTuning,
  liveVersion = null,
  engagementId,
}: {
  /** True once the engine has produced a band for the inputs in this
   *  session (`preflight.computed`) — the same body Finalize will freeze.
   *  VAL-STATE-PERSIST-1 R1: this is NOT the engagement's persisted band.
   *  Only Finalize writes that column pair, so gating on it here made
   *  Record unreachable — it required the exact thing only Record itself
   *  can produce. */
  hasComputedValue: boolean
  /** True for a conclusion-of-value engagement. False for a calculation one,
   *  where a `valuation_owner` may record without holding `canConclude`. */
  concluded: boolean
  canConclude: boolean
  userName?: string | undefined
  /** Stored role code; shown in words via ROLE_LABELS. */
  userRole?: string | undefined
  hasTuningInputs: boolean
  preflight: PreflightResponse | null
  preflightError: string | null
  onBackToTuning: () => void
  /** Live (status: 'final') snapshot version, from the caller's own
   *  snapshot-history fetch. `null`/omitted keeps the heading off — this
   *  file does not derive the number itself. */
  liveVersion?: number | null
  /** When set, the key-people item links to this engagement's Company screen. */
  engagementId?: string | undefined
}) {
  const allBlockers = preflight?.blockers ?? []
  const keyPeopleBlocked = allBlockers.includes(KEY_PEOPLE_BLOCK_TEXT)
  const blockers = allBlockers.filter(b => b !== KEY_PEOPLE_BLOCK_TEXT)
  // Key people has its own gate below; when it is the only blocker the engine check itself is clean.
  const engineClean = !!preflight && (preflight.ok || (keyPeopleBlocked && blockers.length === 0))
  const warnings = preflight?.warnings ?? []
  // Never print an email: empty or email-shaped names count as missing.
  const signerName0 = userName && !userName.includes('@') ? userName : 'this account'
  const roleWords = userRole ? (ROLE_LABELS[userRole] ?? '') : ''
  const signerName = roleWords ? `${signerName0} (${roleWords})` : signerName0

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {liveVersion !== null ? `To finalize v${liveVersion + 1}:` : 'Gates'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Gate
          state={hasComputedValue ? 'ok' : 'blocked'}
          label="Value calculated for these inputs"
          detail={hasComputedValue
            ? 'The engine has computed a band for the methodology inputs in this session.'
            : `${NEXT_STEP_LABELS.methodology} on the Methodology screen — the engine has not returned a computed band for this session yet.`}
        />
        <Gate
          state={concluded ? (canConclude ? 'ok' : 'blocked') : 'ok'}
          label={concluded ? 'Role can finalize' : 'Role can record'}
          detail={concluded
            ? (canConclude
              ? `Signed in as ${signerName} — a conclusion role.`
              : 'A conclusion of value may only be asserted by a credentialed Valuation Signer role. Your role may run calculation engagements.')
            : (canConclude
              ? `Signed in as ${signerName} — recording and signing this calculation.`
              : `Signed in as ${signerName} — recording this estimate.`)}
        />

        {!hasTuningInputs ? (
          <>
            <Gate
              state="blocked"
              label="Inputs saved for this engagement"
              detail="Nothing is saved yet — open Intake, then Methodology."
            />
            <Button variant="outline" size="sm" onClick={onBackToTuning}>
              Back to Methodology
            </Button>
          </>
        ) : preflightError ? (
          <Gate
            state="unknown"
            label="Engine check did not return"
            detail={preflightError}
          />
        ) : !preflight ? (
          <Gate
            state="unknown"
            label="Engine check in progress"
            detail="Running the same computation Finalize will run."
          />
        ) : (
          <>
            <Gate
              state={engineClean ? 'ok' : 'blocked'}
              label="Engine check returned"
              detail={engineClean
                ? 'No blockers. The same computation Finalize runs returned clean.'
                : blockers.join(' · ')}
            />
            {keyPeopleBlocked && (
              <>
                <Gate state="blocked" label="Key people entered" detail={KEY_PEOPLE_BLOCK_TEXT} />
                {engagementId && (
                  <Link className="text-sm font-medium text-blue-700 underline" to={nextStepPath('profile', engagementId) ?? '/valuation'}>
                    Go to the Company screen
                  </Link>
                )}
              </>
            )}
            {warnings.length > 0 && (
              <Gate
                state="unknown"
                label="Warnings recorded with the signature, not blocking"
                detail={warnings.join(' · ')}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export function FinalizeCard({
  methodologyReason,
  rationale,
  onRationaleChange,
  canFinalize,
  blockedReason,
  finalizing,
  onFinalize,
  liveVersion,
  willSign,
  reissueChanged,
  changedFieldLabels,
  delivered = false,
  tuned = false,
}: {
  /** The methodology is tuned for this engagement: only then is a Methodology reason required. */
  tuned?: boolean
  /** True once the live version is signed AND its document is on file: the card is
   *  then the amendment form behind "Start a new version" (no version numbers). */
  delivered?: boolean
  /** The reason typed on Methodology, shown read-only above the sign-off box. */
  methodologyReason: string
  rationale: string
  onRationaleChange: (value: string) => void
  canFinalize: boolean
  blockedReason: string | null
  finalizing: boolean
  onFinalize: () => void
  liveVersion: number | null
  /** True when this actor's finalize call both records AND signs (a
   *  credentialed role). False means it records only — VAL-SIGNOFF-GATE-1:
   *  an owner may record a calculation but not sign or print it. */
  willSign: boolean
  /**
   * VAL-REISSUE-GUARD-1 R3. `null` when this is a first finalize
   * (`liveVersion === null`) — the guard only applies to a re-finalize, and
   * a first finalize always writes v1. Otherwise the caller's client-side
   * mirror of `reissue_delta()`: `false` means nothing that can move the
   * number has changed since the live version.
   */
  reissueChanged: boolean | null
  /** Human labels for what changed, e.g. `['normalized earnings', '3 driver
   *  answers']` — only meaningful when `reissueChanged` is `true`. */
  changedFieldLabels: string[]
}) {
  // The server's own check in finalize.py is authoritative — this is a
  // convenience so the valuator sees "nothing to re-issue" before pressing
  // the button, not instead of the 409 the server still guards with.
  const noChangeMessage = noChangeText(liveVersion, reissueChanged)
  const changedSummary = liveVersion !== null && reissueChanged === true && changedFieldLabels.length > 0
    ? `Changed since v${liveVersion}: ${changedFieldLabels.join(', ')}.`
    : null

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {liveVersion === null
            ? (willSign ? 'Finalize' : 'Record')
            : delivered ? 'New version' : `Re-finalize (v${liveVersion} is live)`}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-gray-500">
          {liveVersion === null
            ? (willSign
              ? 'Finalizing freezes the inputs, the methodology config and the computed value into a reproducible record, and signs it as you. Nothing above is stored until you do.'
              : 'Recording freezes the inputs, the methodology config and the computed value into a reproducible record, attributed to you. This records the calculation — it does not sign it, and it does not unlock document production. A credentialed reviewer can re-finalize this engagement to sign it.')
            : delivered
              ? AMENDMENT_LINE
              : `Re-finalizing writes v${liveVersion + 1} and marks v${liveVersion} superseded. The prior version is kept in full — it is not overwritten and it is not deleted.`}
        </p>

        {changedSummary && (
          <p className="text-xs font-medium text-gray-700">{changedSummary}</p>
        )}

        <div>
          <p className="text-sm font-medium text-gray-900">Methodology reason</p>
          <p className="mb-3 whitespace-pre-wrap text-sm text-gray-700">
            {methodologyReason.trim() ? methodologyReason : 'None recorded'}
          </p>
          <label htmlFor="signoff-rationale" className="mb-1 block text-sm font-medium text-gray-900">
            Sign-off reason
          </label>
          <Textarea
            id="signoff-rationale"
            rows={4}
            value={rationale}
            onChange={e => onRationaleChange(e.target.value)}
            placeholder="Your reason for signing this version."
          />
          <p className="mt-1 text-xs text-gray-500">
            Frozen verbatim into the record and reproduced in the deliverable.
            {tuned && ' A tuned methodology cannot be signed without one.'}
          </p>
        </div>

        <Button
          className="w-full"
          disabled={!canFinalize || finalizing || noChangeMessage !== null}
          onClick={onFinalize}
        >
          {delivered && !finalizing ? 'Re-finalize' : finalizeLabel(finalizing, liveVersion, willSign)}
        </Button>
        {(blockedReason || noChangeMessage) && (
          <p className="text-xs text-amber-800">{blockedReason ?? noChangeMessage}</p>
        )}
      </CardContent>
    </Card>
  )
}

/** VAL-FINALIZE-DONE-1: signed AND a document on file. Approved wording, used verbatim. */
export const DELIVERED_BAR_TEXT = 'Signed. The documents are ready.'
/** VAL-FINALIZE-STATE-2 (owner approved 2026-10-13): the bar's three signed states and the Produce reason. */
export const changedBarText = (v: number) => `You changed inputs since v${v}. They are not signed yet.`
export const producedPendingBarText = (v: number) => `v${v} is signed. Produce the documents.`
export const producedReasonText = (v: number | null) => `Documents for v${v} are on file. Change an input to start a new version.`
export const START_NEW_VERSION = 'Start a new version'
export const AMENDMENT_LINE = 'A new version records changes as an amendment. The signed report stays on file unchanged.'

/** FIX_VAL-SIGNOFF-MESSAGES-1 M2: files on file only count as delivered when the server says the
 *  live version can be produced (signer-signed) — an owner's paid Snapshot files do not. */
export function isDelivered(liveVersion: number | null, documentsOnFile: boolean, canProduce: boolean): boolean {
  return liveVersion !== null && documentsOnFile && canProduce
}

/** The one no-change wording — the Finalize card and the sticky Next step bar both show it. */
export function noChangeText(liveVersion: number | null, reissueChanged: boolean | null): string | null {
  return liveVersion !== null && reissueChanged === false
    ? `Nothing has changed since v${liveVersion}. Change an input on Methodology to re-issue.`
    : null
}

/** The one wording of the finalize button — the sticky Next step bar reuses it. */
export function finalizeLabel(finalizing: boolean, liveVersion: number | null, willSign: boolean): string {
  if (finalizing) return 'Freezing…'
  if (liveVersion === null) return willSign ? 'Finalize and sign' : 'Record calculation'
  return `Re-finalize as v${liveVersion + 1}`
}

export function ProduceDocumentCard({
  engagementId,
  canProduce,
  note,
  onProduce,
  producing,
  documents,
  pdfStatus,
  concluded,
  liveVersion,
  documentsOnFile,
}: {
  engagementId: string
  canProduce: boolean
  note: string
  onProduce: () => void
  producing: boolean
  /** Every issued row for this engagement, oldest first as the API sends them. */
  documents: ValuationDocumentRow[]
  /** The produce response's `pdf_status`, so a missing PDF row shows WHY. */
  pdfStatus: string | null
  /** value_kind === 'concluded' — the frozen record is then a conclusion of value. */
  concluded: boolean
  /** Live snapshot version; null before the first finalize. */
  liveVersion: number | null
  /** Server says a retrievable .docx AND .pdf are on file for the live version. */
  documentsOnFile: boolean
}) {
  const liveRows = documents.filter(d => d.snapshot_version === liveVersion)
  const pdfMissing =
    liveVersion !== null && liveRows.length > 0 && !liveRows.some(d => d.media_type === 'application/pdf')

  return (
    <Card>
      <CardHeader><CardTitle>Produce document</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <Gate
          state={canProduce ? 'ok' : 'blocked'}
          label={canProduce ? 'Unlocked' : 'Locked'}
          detail={note}
        />
        <Button
          className="w-full"
          disabled={!canProduce || producing || documentsOnFile}
          onClick={onProduce}
        >
          {producing ? 'Producing…' : 'Produce document'}
        </Button>
        <EmailDocumentButton
          engagementId={engagementId}
          liveVersion={liveVersion}
          documentsOnFile={documentsOnFile}
        />
        {documentsOnFile && canProduce && (
          <p className="text-xs text-gray-600">
            {producedReasonText(liveVersion)}
          </p>
        )}
        {canProduce && !documentsOnFile && documents.length === 0 && (
          <p className="text-xs text-gray-600">
            The document is rendered from this frozen record — the weights, driver answers,
            benchmark row, inputs, rationale and signature that produced the number are printed
            with it.{' '}
            {concluded
              ? 'It is a CONCLUSION OF VALUE under an SSVS No. 1 valuation engagement.'
              : 'It is a CALCULATED VALUE under an SSVS No. 1 calculation engagement, never a conclusion of value.'}
          </p>
        )}
        {documents.length > 0 && (
          <div className="space-y-2 border-t pt-3">
            <IssuedDocumentList
              engagementId={engagementId}
              documents={documents}
              liveVersion={liveVersion}
            />
            {pdfMissing && (
              <p className="text-xs text-amber-800">
                PDF not available for version {liveVersion}. The Word document above is the
                signed record.{pdfStatus ? ` ${pdfStatus}` : ''}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
